import { useState } from "react";
import { CalendarIcon } from "lucide-react";
import { fr } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLang } from "../i18n/LanguageContext";

// Sélecteur de date localisé (FR/EN) — remplace l'input date natif de la caisse.
// value/onChange au format « AAAA-MM-JJ » (inchangé pour le backend).
export default function DatePicker({ value, onChange, minDate, testid, placeholder }) {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(`${value}T12:00:00`) : undefined;

  const label = selected
    ? selected.toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA",
        { year: "numeric", month: "long", day: "numeric" })
    : placeholder;

  const pick = (d) => {
    if (!d) return;
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    onChange(iso);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`w-full inline-flex items-center gap-3 bg-surface border border-ivory/15 rounded-lg px-4 py-3.5 text-sm text-left focus:border-gold focus:outline-none transition-colors duration-300 ${selected ? "text-ivory" : "text-ivory/60"}`}
        >
          <CalendarIcon className="w-4 h-4 text-gold shrink-0" />
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 bg-surface border-gold/25 text-ivory">
        <Calendar
          mode="single"
          locale={lang === "en" ? undefined : fr}
          selected={selected}
          onSelect={pick}
          disabled={{ before: minDate }}
          defaultMonth={selected || minDate}
          data-testid={`${testid}-calendar`}
          classNames={{
            caption_label: "text-sm font-medium text-ivory",
            head_cell: "text-ivory/55 rounded-md w-9 font-normal text-[0.75rem]",
            day: "h-9 w-9 p-0 font-normal text-ivory/85 hover:bg-gold/15 hover:text-gold rounded-md transition-colors duration-150 aria-selected:opacity-100",
            day_selected: "bg-gold text-ink font-bold hover:bg-gold hover:text-ink focus:bg-gold focus:text-ink",
            day_today: "border border-gold/40 text-gold",
            day_disabled: "text-ivory/25 opacity-50 hover:bg-transparent hover:text-ivory/25",
            day_outside: "text-ivory/30",
            nav_button: "h-7 w-7 bg-transparent p-0 text-ivory/60 hover:text-gold border border-ivory/15 rounded-md inline-flex items-center justify-center transition-colors duration-150",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
